/**
 * The road as a SHAPE: the exported path turned into continuous runs, with the width of the road at every
 * point and the line of its boundary on either side. No canvas and no look in here — what the surface is
 * painted WITH is the website painter's business (gpo2_website/src/client/src/lib/trackEditor/
 * trackPainter.js), and this is only where the road IS.
 *
 * It is shared, and that is the point of the split: the painter draws its edge lines and asphalt from
 * these runs, and the game keeps the same geometry for what it draws on top of the track (the surroundings
 * painter, the debug overlay that shows the road the physics tests). One implementation, so a road can
 * never be painted one way and measured another.
 *
 * Exported as a verbatim copy to the website (see `npm run sync:painter`): the editor previews the road
 * the game shows. Edit HERE, then re-sync.
 */
import { samplePiece, widthOf } from './trackGeometry.js';

const TAU = Math.PI * 2;

// How far a corner may be "spiked" by a miter, as a multiple of the half-width: a right angle is
// 1.41, so this keeps every ordinary corner exact and chamfers the sharper ones (a 143° corner
// would otherwise put its corner point two radii AWAY from the road). Used by the painter too, which
// insets the asphalt's own lines the same way (see paintPieceAsphalt's ring inset).
export const MITER_LIMIT = 1.6;
// How fast the road may change width along its length (width units per world unit) where a narrower
// piece meets a wider one: the narrower side flares out to the wider one over this ramp instead of
// stepping. A step is what makes two edge lines that should be one curve slide sideways.
const WIDTH_SLOPE = 0.25;

export function unit(dx, dy) {
  const len = Math.hypot(dx, dy);
  return len > 1e-6 ? { x: dx / len, y: dy / len } : null;
}

/** Drops consecutive points at (nearly) the same spot: a zero-length piece has no direction. */
export function withoutDuplicates(run, tolerance = 0.01) {
  const out = [];
  for (const point of run) {
    const prev = out[out.length - 1];
    if (!prev || Math.hypot(point.x - prev.x, point.y - prev.y) > tolerance) out.push(point);
  }
  return out;
}

/**
 * Both cleanups the exported paths need, run until they stop finding anything: dropping a spike can
 * leave two of its neighbours nearly on top of each other (a 2px piece, whose joints the boundary
 * still tries to miter, 200px off the road), and deduplicating can expose a new spike.
 */
export function cleanRun(run, fallbackWidth) {
  let current = run;
  for (let pass = 0; pass < 3; pass++) {
    const cleaned = withoutSpikes(withoutDuplicates(current, 3), fallbackWidth);
    if (cleaned.length === current.length) return cleaned;
    current = cleaned;
  }
  return current;
}

/**
 * The export can leave a point that doubles back for a few pixels — a reversed piece stitched into
 * the path (hot_road has one 5px out-and-back mid-corner). That is what draws the bulge: the road
 * turns around inside its own width, so the painter paints a disc there and rings it with a white
 * circle. A reversal whose two neighbours are less than half a road apart is noise, not a hairpin
 * (a real one has its legs on either side of it), so the detour point is dropped.
 */
function withoutSpikes(run, fallbackWidth) {
  const out = [];
  for (let i = 0; i < run.length; i++) {
    const previous = out[out.length - 1];
    const next = run[i + 1];
    if (previous && next && isSpikeDetour(previous, run[i], next, fallbackWidth)) continue;
    out.push(run[i]);
  }
  return out;
}

/**
 * A turn that comes back within half a road width. The `dot` threshold is loose on purpose (~120°)
 * and it has to be paired with the short chord: a real corner of that sharpness has its two
 * neighbours a long way apart, so it can never be mistaken for this.
 */
function isSpikeDetour(previous, point, next, fallbackWidth) {
  if (Math.hypot(next.x - previous.x, next.y - previous.y) > widthOf(point, fallbackWidth) / 2) return false;
  const u1 = unit(point.x - previous.x, point.y - previous.y);
  const u2 = unit(next.x - point.x, next.y - point.y);
  return !!u1 && !!u2 && u1.x * u2.x + u1.y * u2.y < -0.5;
}

/** ~170°: the road doesn't turn here, it goes back the way it came. */
export function isReversal(a, b, c) {
  const u1 = unit(b.x - a.x, b.y - a.y);
  const u2 = unit(c.x - b.x, c.y - b.y);
  return !!u1 && !!u2 && u1.x * u2.x + u1.y * u2.y < -0.985;
}

/**
 * Splits the exported path into its pieces. A `gap` point does NOT always mean a hole: the editor's
 * export marks one whenever two consecutive pieces don't share the exact same rounded endpoint, so a
 * 5px mismatch — with a width that also changes by a few units there — becomes a hard break. Two ends
 * closer than their two end discs are is one road, so they're merged here; pieces genuinely far apart
 * (a missing bridge, a real jump) stay separate, and nothing is invented past that.
 */
function splitPieces(path, fallbackWidth) {
  const pieces = [];
  let current = [];
  const flush = () => { if (current.length) pieces.push(current); current = []; };

  for (let i = 0; i < (path?.length ?? 0); i++) {
    const point = path[i];
    const previous = current[current.length - 1];
    if (i > 0 && point.gap && !(previous && endsMeet(previous, point, fallbackWidth))) flush();
    current.push(point);
  }
  flush();
  return pieces;
}

/**
 * Chains the pieces into continuous roads BY GEOMETRY, because the order they are exported in is not
 * necessarily the order they are driven in — and a piece can be saved backwards. Either way the path
 * jumps from one end of the track to another: drawn as-is the road stops in mid-air, the piece that
 * actually continues it overlaps it in a bulge, and the two edge lines don't meet (hot_road's first
 * corner, 2026-09-19).
 *
 * So: whichever unused piece has an end closest to where the current road stops continues it — turned
 * around if that is the end that fits — as long as the two ends are within a road width of each
 * other. What is left after that is a genuinely separate road, and starts its own run.
 */
export function chainRuns(pieces, fallbackWidth, areas = []) {
  const pending = (pieces ?? []).filter((piece) => piece.length > 1);
  const runs = [];

  while (pending.length) {
    const run = pending.shift().slice();
    for (let pass = 0; pass < 2; pass++) {
      for (;;) {
        const atStart = pass === 1;
        const end = atStart ? run[0] : run[run.length - 1];
        let best = -1;
        let bestDistance = Infinity;
        let flip = false;

        pending.forEach((piece, index) => {
          // When appending, the piece's start must end up touching the run; when prepending, its END
          // must — hence the piece is turned around in opposite cases.
          const ends = [
            { point: piece[0], atPieceStart: true },
            { point: piece[piece.length - 1], atPieceStart: false },
          ];
          for (const { point, atPieceStart } of ends) {
            const distance = Math.hypot(point.x - end.x, point.y - end.y);
            if (distance > (widthOf(end, fallbackWidth) + widthOf(point, fallbackWidth)) / 2) continue;
            // A gap the author filled with a road area is a corner the corridors cannot express: the
            // area IS the asphalt there, so reaching across it would draw a straight band whose edges
            // cut clean through that corner. Only a real gap (nothing already paved in between) gets
            // bridged.
            if (distance > 2
              && coveredByArea((end.x + point.x) / 2, (end.y + point.y) / 2, areas)) continue;
            if (distance >= bestDistance) continue;
            best = index;
            bestDistance = distance;
            flip = atStart === atPieceStart;
          }
        });
        if (best < 0) break;

        const piece = pending.splice(best, 1)[0];
        const points = flip ? piece.slice().reverse() : piece;
        if (atStart) run.unshift(...points);
        else run.push(...points);
      }
    }
    const first = run[0];
    const last = run[run.length - 1];
    const gap = Math.hypot(last.x - first.x, last.y - first.y);
    if (run.length > 2 && gap > 2 && coveredByArea((first.x + last.x) / 2, (first.y + last.y) / 2, areas)) {
      // A circuit left open with the gap paved by a road area: welding it would draw a straight road
      // across that corner — the same trap as bridging two pieces above, and the corner the area is
      // there to express. Open it stays; both ends are capped, under the area's asphalt.
      runs.push(run);
      continue;
    }
    if (run.length > 2 && endsMeet(first, last, fallbackWidth)) {
      runs.push(closeRing(run, fallbackWidth));
      continue;
    }
    runs.push(run);
  }
  return runs;
}

/** How much the road turns at `point`, 0 being perfectly straight on. */
function turnAt(previous, point, next) {
  const ax = point.x - previous.x;
  const ay = point.y - previous.y;
  const bx = next.x - point.x;
  const by = next.y - point.y;
  const scale = Math.hypot(ax, ay) * Math.hypot(bx, by);
  if (!scale) return Math.PI;
  return Math.acos(Math.max(-1, Math.min(1, (ax * bx + ay * by) / scale)));
}

/**
 * Welds the two ends of a circuit into a ring, and drops the seam where the road doesn't turn.
 *
 * The export leaves circuits open — the last piece stops where the first one starts, tens of px apart
 * with no road in between — so the road is capped there: a flat asphalt end, a wedge missing on the
 * outside of the bend, and the two edge lines stopping in mid-air instead of running around the
 * corner. Closing it makes that junction an ordinary vertex.
 *
 * The seam (the point where the run starts and ends, the only vertex with no join and no disc) is
 * then moved to the straightest vertex it can find: on a straight length it is indistinguishable
 * from an ordinary pair of points, while on a bend it shows as a kink in both edge lines — there the
 * two ends disagree by the width difference, which is doubly visible on a road that also changes
 * width right there (hot_road's first corner: the 180 pit straight into the 210 bend).
 */
function closeRing(run, fallbackWidth) {
  if (run.length < 3 || !endsMeet(run[0], run[run.length - 1], fallbackWidth)) return run;

  let seam = 0;
  let straightest = Infinity;
  for (let i = 0; i < run.length; i++) {
    const turn = turnAt(run[(i - 1 + run.length) % run.length], run[i], run[(i + 1) % run.length]);
    if (turn < straightest) {
      straightest = turn;
      seam = i;
    }
  }

  const ordered = run.slice(seam).concat(run.slice(0, seam));
  ordered.push({ ...ordered[0] });
  return ordered;
}

/**
 * Splits the pieces whose width changes into sub-pieces, so that the change is actually DRAWN. A
 * stroke has one width, so a piece going from 180 to 210 was painted as a 195-wide rectangle with a
 * step of 15 at each end — the bulge seen where the road widens into a junction.
 */
function subdivide(run, widths, maxStep = 40) {
  const points = [];
  for (let i = 0; i < run.length - 1; i++) {
    points.push({ x: run[i].x, y: run[i].y, width: widths[i] });
    const length = Math.hypot(run[i + 1].x - run[i].x, run[i + 1].y - run[i].y);
    // Only worth splitting a piece long enough for the ramp to be visible anyway: the export has runs
    // of 4-6px points, and cutting THOSE in half puts vertices closer together than the offset line
    // can handle (the two edge lines then flip back and forth at every one of them).
    const steps = Math.abs(widths[i + 1] - widths[i]) < 1 ? 1 : Math.min(64, Math.floor(length / maxStep));
    for (let s = 1; s < steps; s++) {
      const t = s / steps;
      points.push({
        x: run[i].x + (run[i + 1].x - run[i].x) * t,
        y: run[i].y + (run[i + 1].y - run[i].y) * t,
        width: widths[i] + (widths[i + 1] - widths[i]) * t,
      });
    }
  }
  const last = run[run.length - 1];
  points.push({ x: last.x, y: last.y, width: widths[widths.length - 1] });
  return points;
}

/**
 * The road as it should be drawn: the exported path cut into pieces, chained into continuous runs
 * (chainRuns), cleaned of the spikes and loops the export leaves in it, with every width change
 * spread over the length (blendedWidths) and finely enough split to be painted (subdivide).
 * Every point carries its `width`, so the callers only have to stroke them.
 */
export function roadRuns(path, fallbackWidth, areas = []) {
  const runs = [];
  for (const chunk of chainRuns(splitPieces(path, fallbackWidth), fallbackWidth, areas)) {
    const cleaned = cleanRun(chunk, fallbackWidth);
    if (cleaned.length < 2) continue;
    const widths = blendedWidths(cleaned, fallbackWidth);
    // The seam of a ring is the same physical point twice, so it cannot carry two widths: taking the
    // first one avoids a step of half the difference in both edge lines right at the seam.
    if (isRing(cleaned)) widths[widths.length - 1] = widths[0];
    runs.push(subdivide(cleaned, widths));
  }
  return runs;
}

/** True when the run's first and last point are the same place (a circuit closed by closeRing). */
function isRing(run) {
  const first = run[0];
  const last = run[run.length - 1];
  return run.length > 3 && first.x === last.x && first.y === last.y;
}

function endsMeet(a, b, fallbackWidth) {
  const reach = (widthOf(a, fallbackWidth) + widthOf(b, fallbackWidth)) / 2;
  return Math.hypot(b.x - a.x, b.y - a.y) <= reach;
}

/**
 * Per-point width with every step rounded off (see WIDTH_SLOPE): two pieces of different width used
 * to meet with a jolt, which shows up on both edge lines as a sideways jump. The narrower side is
 * what moves — it flares out to the wider one — since pinching the road at a junction reads as a
 * mistake, and the smoothing passes take the knee off the ends of the ramp.
 */
export function blendedWidths(run, fallbackWidth) {
  const n = run.length;
  const widths = run.map((point) => widthOf(point, fallbackWidth));
  if (n < 3) return widths;

  const reach = (i) => Math.hypot(run[i].x - run[i - 1].x, run[i].y - run[i - 1].y) * WIDTH_SLOPE;
  for (let i = 1; i < n; i++) widths[i] = Math.max(widths[i], widths[i - 1] - reach(i));
  for (let i = n - 2; i >= 0; i--) widths[i] = Math.max(widths[i], widths[i + 1] - reach(i + 1));
  for (let pass = 0; pass < 3; pass++) {
    for (let i = 1; i < n - 1; i++) {
      widths[i] = (widths[i - 1] + 2 * widths[i] + widths[i + 1]) / 4;
    }
  }
  return widths;
}

/**
 * Boundary of the road on one side: the line that runs at `halfWidth - shrink` from the centerline,
 * with every vertex joined properly.
 *
 * The joins are the whole point of this function. Offsetting each point on its own (its own
 * perpendicular, direction averaged with its neighbours) puts a vertex slightly *inside* the corner
 * on the outside and crosses the two pieces over each other on the inside — a corner reads as a
 * zig-zag with a spike, instead of the arc a real track edge has. So, per joint:
 *  - the two pieces' offset lines are extended to where they cross (a **miter**). If that crossing
 *    lies BEHIND the vertex, it is the inside of the corner: both pieces are trimmed back to it.
 *    Two such joints closer together than their trims even collapse into one, which is why the
 *    boundary is built piece by piece (see below).
 *  - if it lies AHEAD, it is the outside, and the pieces are joined by an **arc** around the vertex.
 *    That is exactly the asphalt's own edge there: paintRoad drops a disc of radius halfWidth on
 *    every interior point, so the corner of the corridor is that disc, and the arc is it, inset.
 *  - a vertex where the path doubles back on itself (the editor can produce one) is not a bend at
 *    all: the line just ends there, since wrapping a circle around the tip paints a ring in the
 *    middle of the asphalt.
 *  - a vertex sharper than MITER_LIMIT is chamfered instead: its corner point would sit far off the
 *    road, and following it paints a long spike out of the edge.
 *
 * `shrink` is measured from the asphalt edge: positive for the white line (its inset plus half its
 * own width), negative to draw something OUTSIDE the road (run-off, kerbs).
 */
export function roadBoundary(run, side, fallbackWidth, shrink, opts = {}) {
  const points = withoutDuplicates(run ?? []);
  // A barrier wants its corners SQUARE (a wall bends, it does not curve round): the same miter the
  // concave corners get, used on the convex ones too. The asphalt wants the arc, because the asphalt
  // really is stroked with round joins and the line has to be that edge, inset.
  const mitre = opts.mitre === true;
  const n = points.length;
  const out = [];
  if (n < 2) return out;

  const radiusAt = (i) => widthOf(points[i], fallbackWidth) / 2 - shrink;
  const offsetAt = (i, dir) => {
    const r = radiusAt(i);
    if (!dir || !(r > 0)) return null;
    return { x: points[i].x - dir.y * r * side, y: points[i].y + dir.x * r * side };
  };
  const dirAt = (a, b) => unit(points[b].x - points[a].x, points[b].y - points[a].y);

  // The boundary is emitted piece by piece rather than point by point, so a joint can REPLACE the
  // ends of the two pieces it joins. That is what trims them back to the miter — and what collapses
  // the two joints together when a piece is shorter than the trims both of them ask for (a 13px
  // piece between two corners otherwise makes the line run forward, back and forward again).
  let entryPlaced = false;
  for (let i = 0; i < n - 1; i++) {
    const dir = dirAt(i, i + 1);
    const entry = offsetAt(i, dir);
    const exit = offsetAt(i + 1, dir);
    let skipExit = false;
    if (!entry || !exit) {
      out.push(null); // too narrow for a line here: break instead of jumping across
      entryPlaced = false;
      continue;
    }

    if (i > 0 && !entryPlaced) {
      const dirIn = dirAt(i - 1, i);
      const from = offsetAt(i, dirIn);
      const cross = dirIn.x * dir.y - dirIn.y * dir.x;
      const dot = dirIn.x * dir.x + dirIn.y * dir.y;

      if (from && Math.abs(cross) < 1e-12) {
        if (dot > 0) {
          out[out.length - 1] = entry; // straight through: the two ends are the same point
          entryPlaced = true;
        } else {
          // Doubled back: the road doesn't bend here, it ends. The line stops instead of wrapping
          // the tip — a circle traced around it would be a ring of paint in the middle of the road.
          out.push(null);
        }
      } else if (from) {
        // Where the incoming piece's offset line meets the outgoing piece's, along dirIn.
        const t = ((entry.x - from.x) * dir.y - (entry.y - from.y) * dir.x) / cross;
        const miter = { x: from.x + dirIn.x * t, y: from.y + dirIn.y * t };
        const len = Math.hypot(miter.x - points[i].x, miter.y - points[i].y);
        if (t > 0) {
          if (mitre && len <= radiusAt(i) * MITER_LIMIT) {
            // Square corner: the two offset edges meet at the miter point, and the boundary turns there.
            // (Past the limit the corner would stick out too far, and the arc is the fallback.)
            out.push(miter);
            entryPlaced = true;
          } else {
            // The arc is drawn from where the line actually is (the previous piece's end, or the miter
            // the joint before swallowed it) to this piece's end, taking the shorter way round: that is
            // the round join, and it can never start by stepping back.
            const placed = out[out.length - 1];
            arcBetween(out, points[i], placed && typeof placed === 'object' ? placed : from, entry, radiusAt(i));
          }
        } else if (len > radiusAt(i) * MITER_LIMIT) {
          // Too sharp to miter: the corner point would sit far off the road (two radii away on a
          // 143° turn) and following it paints a long spike. The line meets its own end instead — a
          // chamfered corner — which cuts the inside of the bend by a few units and keeps the line
          // in one piece.
        } else {
          // The incoming piece may already have put points on the line PAST this miter (a piece
          // shorter than the trims both of its joints ask for): drop them, which is how the piece
          // and its two joints collapse into the single corner they really are, instead of the line
          // running forward, back and forward again.
          const beyond = (point) =>
            (point.x - points[i].x) * dirIn.x + (point.y - points[i].y) * dirIn.y > t;
          while (out.length && out[out.length - 1] && beyond(out[out.length - 1])) out.pop();
          out.push(miter);
          entryPlaced = true;
          // A piece shorter than this miter's trim is swallowed by the corner altogether: its far
          // end is behind the miter, and pushing it too would put the line back where it came from
          // (the same overlap as above, at the piece's other end).
          const pieceLength = Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y);
          skipExit = (miter.x - points[i].x) * dir.x + (miter.y - points[i].y) * dir.y > pieceLength;
        }
      }
    }

    if (!entryPlaced) out.push(entry);
    if (!skipExit) out.push(exit);
    entryPlaced = false;
  }
  return withoutBoundarySpikes(out);
}

/**
 * Drops the points where the finished line doubles back on itself. Every joint this function makes
 * turns by less than ~100° (miters are limited to MITER_LIMIT, arcs are sampled, sharper corners are
 * chamfered), so a reversal in the RESULT can only come from a kink or a loop the exported path
 * itself has — and following one paints a line that goes out and comes back for tens of units.
 */
export function withoutBoundarySpikes(points) {
  const out = [];
  for (const point of points) {
    if (!point) {
      out.push(null);
      continue;
    }
    while (out.length >= 2) {
      const last = out[out.length - 1];
      const before = out[out.length - 2];
      if (!last || !before) break;
      const u1 = unit(last.x - before.x, last.y - before.y);
      const u2 = unit(point.x - last.x, point.y - last.y);
      if (!u1 || !u2 || u1.x * u2.x + u1.y * u2.y >= -0.7) break;
      out.pop();
    }
    out.push(point);
  }
  return out;
}

/** Appends the intermediate points of an arc around `center`, from `startPoint`'s angle to
 * `endPoint`'s, taking the shorter way round. */
function arcBetween(out, center, startPoint, endPoint, radius) {
  const start = Math.atan2(startPoint.y - center.y, startPoint.x - center.x);
  const end = Math.atan2(endPoint.y - center.y, endPoint.x - center.x);
  let sweep = end - start;
  while (sweep > Math.PI) sweep -= TAU;
  while (sweep < -Math.PI) sweep += TAU;
  const steps = Math.max(2, Math.min(24, Math.ceil(Math.abs(sweep) / 0.15)));
  for (let s = 1; s < steps; s++) {
    const angle = start + (sweep * s) / steps;
    out.push({ x: center.x + Math.cos(angle) * radius, y: center.y + Math.sin(angle) * radius });
  }
}

/** The asphalt areas as plain point rings, which is what the corridor has to be checked against. */
export function areaRings(track) {
  return (track?.pieces ?? [])
    .filter((piece) => piece.kind === 'area')
    .map((piece) => samplePiece(piece).points)
    .filter((points) => points.length >= 3);
}

/** True when (x, y) falls inside one of the area rings (ray casting). */
function coveredByArea(x, y, areas) {
  for (const points of areas) {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
      const a = points[i], b = points[j];
      if ((a.y > y) !== (b.y > y) && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
    }
    if (inside) return true;
  }
  return false;
}
